import { AccountPage, AppAccountsPage, ShellLayout, UsersPage } from "@dl/web-core-js/shell";
import { createRootRoute, createRoute, createRouter, redirect } from "@tanstack/react-router";
import { ArtworkDetailPage } from "./pages/artwork/ArtworkDetailPage";
import { ArtworksPage } from "./pages/artwork/ArtworksPage";
import { CollectionsPage } from "./pages/collection/CollectionsPage";
import { DisplayCollectionDetailPage } from "./pages/collection/DisplayCollectionDetailPage";
import { DisplayCollectionsPage } from "./pages/collection/DisplayCollectionsPage";
import { ShopifyCollectionDetailPage } from "./pages/collection/ShopifyCollectionDetailPage";
import { ShopifyCollectionsPage } from "./pages/collection/ShopifyCollectionsPage";
import { CatalogsPage } from "./pages/config/CatalogsPage";
import { ClientDetailPage } from "./pages/config/ClientDetailPage";
import { ClientsPage } from "./pages/config/ClientsPage";
import { DecorsPage } from "./pages/config/DecorsPage";
import { PrintSettingsDetailPage } from "./pages/config/PrintSettingsDetailPage";
import { PrintSettingsPage } from "./pages/config/PrintSettingsPage";
import { PublishersPage } from "./pages/config/PublishersPage";
import { PublisherDetailPage } from "./pages/config/PublisherDetailPage";
import { SkuMatchPage } from "./pages/config/SkuMatchPage";
import { StoresPage } from "./pages/config/StoresPage";
import { HomePage } from "./pages/HomePage";
import { JoliscreenPage } from "./pages/joliscreen/JoliscreenPage";
import { OrderDetailPage } from "./pages/order/OrderDetailPage";
import { OrdersPage } from "./pages/order/OrdersPage";
import { ProductCategoriesPage } from "./pages/product/ProductCategoriesPage";
import { ProductDetailPage } from "./pages/product/ProductDetailPage";
import { ProductsPage } from "./pages/product/ProductsPage";
import { ProductionCentersPage } from "./pages/production/ProductionCentersPage";
import { ProductionJobsPage } from "./pages/production/ProductionJobsPage";

/**
 * One route per Vaadin `@Route`, plus a detail route wherever Vaadin swapped
 * the grid for an edit pane.
 *
 * Making the detail a real URL rather than in-page state is the one deliberate
 * departure: a product, an order or an artwork is now linkable, which is what
 * people always wanted from the edit pane.
 *
 * Each route is declared on its own rather than through a loop helper — that
 * is what lets TanStack infer the literal path union, so `navigate({ to })`
 * and `useParams({ from })` are checked against the real tree.
 */
const rootRoute = createRootRoute({ component: ShellLayout });

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: HomePage,
});

const artworksRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/artworks",
  component: ArtworksPage,
});

const artworkDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/artworks/$id",
  component: ArtworkDetailPage,
});

const productsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/product",
  component: ProductsPage,
});

const productDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/product/$id",
  component: ProductDetailPage,
});

const productCategoriesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/product/category",
  component: ProductCategoriesPage,
});

const ordersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/orders",
  component: OrdersPage,
});

const orderDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/orders/$id",
  component: OrderDetailPage,
});

const productionJobsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/production/job",
  component: ProductionJobsPage,
});

// The legacy queue is a switch on the jobs page now; old bookmarks land there.
const legacyJobsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/production/legacy/job",
  beforeLoad: () => {
    throw redirect({ to: "/production/job" });
  },
});

const productionCentersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/production/center",
  component: ProductionCentersPage,
});

const collectionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/collection/collections",
  component: CollectionsPage,
});

const displayCollectionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/collection/display-collections",
  component: DisplayCollectionsPage,
});

const displayCollectionDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/collection/display-collections/$id",
  component: DisplayCollectionDetailPage,
});

const shopifyCollectionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/collection/shopify-collections",
  component: ShopifyCollectionsPage,
});

const shopifyCollectionDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/collection/shopify-collections/$id",
  component: ShopifyCollectionDetailPage,
});

const joliscreenRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/joliscreen/images",
  component: JoliscreenPage,
});

const publishersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/config/publisher",
  component: PublishersPage,
});

const publisherDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/config/publisher/$id",
  component: PublisherDetailPage,
});

// Metadata templates live on the publisher's page now; old bookmarks land on the list.
const metadataTemplatesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/config/publisher-metadata-templates",
  beforeLoad: () => {
    throw redirect({ to: "/config/publisher" });
  },
});

const metadataTemplateDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/config/publisher-metadata-templates/$id",
  beforeLoad: () => {
    throw redirect({ to: "/config/publisher" });
  },
});

const catalogsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/config/catalog",
  component: CatalogsPage,
});

const decorsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/config/decor",
  component: DecorsPage,
});

const storesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/config/store",
  component: StoresPage,
});

const clientsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/config/client",
  component: ClientsPage,
});

const clientDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/config/client/$id",
  component: ClientDetailPage,
});

const printSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/config/print-settings",
  component: PrintSettingsPage,
});

const printSettingsDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/config/print-settings/$id",
  component: PrintSettingsDetailPage,
});

const skuMatchRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/config/sku-match",
  component: SkuMatchPage,
});

const usersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/users",
  component: UsersPage,
});

const appAccountsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/app-accounts",
  component: AppAccountsPage,
});

const accountRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/account",
  component: AccountPage,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  artworksRoute,
  artworkDetailRoute,
  productsRoute,
  productDetailRoute,
  productCategoriesRoute,
  ordersRoute,
  orderDetailRoute,
  productionJobsRoute,
  legacyJobsRoute,
  productionCentersRoute,
  collectionsRoute,
  displayCollectionsRoute,
  displayCollectionDetailRoute,
  shopifyCollectionsRoute,
  shopifyCollectionDetailRoute,
  joliscreenRoute,
  publishersRoute,
  publisherDetailRoute,
  metadataTemplatesRoute,
  metadataTemplateDetailRoute,
  catalogsRoute,
  decorsRoute,
  storesRoute,
  clientsRoute,
  clientDetailRoute,
  printSettingsRoute,
  printSettingsDetailRoute,
  skuMatchRoute,
  usersRoute,
  appAccountsRoute,
  accountRoute,

]);

export const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  scrollRestoration: true,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
